import { type NovelItem, NovelItem_Status } from '@app/_proto/Protos/novels';
import {
    GetSponsorPlansRequest_SortDirection,
    GetSponsorPlansRequest_SortType,
    type SponsorPlanItem,
} from '@app/_proto/Protos/sponsors';
import { ChampionPlanDefaultButton } from '@app/domains/champion/components/ChampionPlanButtonOnly';
import ChampionPlanPriceInfo from '@app/domains/champion/components/ChampionPlanPriceInfo';
import ChampionPlanWithDialog from '@app/domains/champion/containers/ChampionPlanWithDialog';
import useChampionSubscriptions from '@app/domains/champion/hooks/useChampionSubscriptions';
import useSortedAndAuthChampionPlans from '@app/domains/champion/hooks/useSortedAndAuthChampionPlans';
import { useAuth } from '@app/libs/auth';
import { decimalToNumber } from '@app/libs/utils';
import { useMemo } from 'react';

type ChampionForCompletedNovelProps = {
    novel: NovelItem;
    onSelectPlan?: (plan: SponsorPlanItem) => void;
};

export default function ChampionForCompletedNovel({ novel, onSelectPlan }: ChampionForCompletedNovelProps) {
    const { user } = useAuth();

    const {
        activeSubscription: activeChampionSubscription,
        isSuccess: isActiveSubscriptionLoadSuccess,
        isLoading: isActiveSubscriptionLoading,
    } = useChampionSubscriptions({
        novelId: novel?.id,
        enabled: !!user && !!novel?.id && novel.status === NovelItem_Status.Finished,
        keepPreviousData: false,
    });

    const { data: sortedSponsorPlans, isLoading: isChampionPlansLoading } = useSortedAndAuthChampionPlans({
        novelId: novel?.id,
        sortType: GetSponsorPlansRequest_SortType.AdvanceChapters,
        sortDirection: GetSponsorPlansRequest_SortDirection.ASC,
        enabled: novel.status === NovelItem_Status.Finished,
        showModalOnError: false,
    });

    const plan = useMemo(() => {
        if (sortedSponsorPlans && sortedSponsorPlans.length > 0) {
            return sortedSponsorPlans[0];
        }
    }, [sortedSponsorPlans]);

    if (
        !user ||
        !plan ||
        isActiveSubscriptionLoading ||
        isChampionPlansLoading ||
        novel.status !== NovelItem_Status.Finished
    ) {
        return null;
    }

    return (
        <div className="flex flex-col xs:flex-row items-center justify-center mt-16 px-16 py-12 gap-16 rounded-[12px] bg-gray-bg-elevate">
            <div className="flex-1 text-center xs:text-left">
                Obtain access to all chapters for{' '}
                <span className="inline-block">
                    <ChampionPlanPriceInfo
                        price={decimalToNumber(plan.price)}
                        discountInfo={!activeChampionSubscription ? plan.discountInfo : undefined}
                        withDiscountDurationWarning={true}
                        priceProps={{
                            className: '',
                        }}
                        discountedPriceProps={{
                            className: '',
                        }}
                        durationProps={{
                            className: '',
                        }}
                    />
                </span>
            </div>
            <div>
                <ChampionPlanWithDialog
                    plan={plan}
                    currentSubscription={activeChampionSubscription ?? null}
                    onSelectPlan={onSelectPlan}
                    renderChampionPlan={ChampionPlanDefaultButton}
                />
            </div>
        </div>
    );
}
